import {
    Component,
    ChangeDetectionStrategy,
    OnInit,
    ViewChild,
    ChangeDetectorRef,
    OnDestroy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuctionTypeEnum, SellingTypeEnum } from '@classifieds-sell/datalayer';
import { convertToCurrency, FormHelperService, isEditPage, ValidatorPipe } from '@classifieds-sell/shared';
import { MotorsDraftFacade } from '@classifieds-sell/state';
import { Subject } from 'rxjs';
import { DEFAULT_MAX_PRICE } from '../../constants/motors-shared.constants';
import { FeeCalculationFacade } from '../../state/fee-calculation/fee-calculation.facade';
import { MotorsSectionBase } from '../motors-section-base';

@Component({
    selector: 'motors-auction-pricing-section',
    templateUrl: './motors-auction-pricing-section.component.html',
    styleUrls: ['./motors-auction-pricing-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MotorsAuctionPricingSectionComponent
    extends MotorsSectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('motorsAuctionPricingSectionForm')
    motorsAuctionPricingSectionForm: NgForm;
    AuctionTypeEnum = AuctionTypeEnum;
    isEditPage = false;
    isDisableReservePriceInput = false;
    isDisableStartPriceInput = false;
    isDisableBuyNowPriceInput = false;
    isDisableAllowBidsFromAuthenticatedUsersOnly = false;

    constructor(
        public draftFacade: MotorsDraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formHelperService: FormHelperService,
        private _validatorPipe: ValidatorPipe,
        private _activeRoute: ActivatedRoute,
        public feeCalculationFacade: FeeCalculationFacade
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this.isEditPage = isEditPage(this._activeRoute);
            if (this.isEditPage) {
                this.isDisableReservePriceInput = this.isReserveMet;
                this.isDisableBuyNowPriceInput =
                    this.isDisableStartPriceInput =
                        this.isDisableAllowBidsFromAuthenticatedUsersOnly =
                            this.isWithBid || this.isReserveMet;
            }

            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        if (this.draftData.listingDetails.sellingType === SellingTypeEnum.undefined) {
            return true;
        }
        this._formHelperService.validate(this.motorsAuctionPricingSectionForm);
        if (this.motorsAuctionPricingSectionForm.disabled) {
            return true;
        }
        return this.motorsAuctionPricingSectionForm.form.valid;
    }

    dirty(): boolean {
        return this.motorsAuctionPricingSectionForm.form.dirty;
    }

    reservePriceValidation() {
        const minValue = this.draftData.listingDetails.startPrice;
        const maxValue = this.draftData.listingDetails.startPrice / 0.6;
        const isValidRange = !!this.draftData.listingDetails.reservePrice
            && !!this.draftData.listingDetails.startPrice
            && this.draftData.listingDetails.reservePrice >= minValue
            && this.draftData.listingDetails.reservePrice <= maxValue;
        const isIncreased = this.isEditPage
            && this.isWithBid
            && !!this.originalDraftData.listingDetails.reservePrice
            && !!this.draftData.listingDetails.reservePrice
            && this.draftData.listingDetails.reservePrice > this.originalDraftData.listingDetails.reservePrice;
        const isLessThanMaxBidPlusOne = this.isEditPage
            && this.isWithBid && !this.isReserveMet
            && !!this.metadata.maxBidAmount
            && !!this.draftData.listingDetails.reservePrice
            && this.draftData.listingDetails.reservePrice < this.metadata.maxBidAmount + 1;
        let errorMessage = '';

        if (isIncreased) {
            errorMessage = `You cannot increase the reserve price (currently ${convertToCurrency(this.originalDraftData.listingDetails.reservePrice)} when there's a bid.
            Your new reserve must be greater than the current bid ${convertToCurrency(this.metadata.maxBidAmount)})`;
        } else if (isLessThanMaxBidPlusOne) {
            errorMessage = `Your reserve price must be at least $1.00 higher than the leading bid (currently ${convertToCurrency(this.originalDraftData.listingDetails.reservePrice)} when there's a bid.
            Your new reserve must be greater than the current bid ${convertToCurrency(this.metadata.maxBidAmount)})`;
        } else if (!isValidRange) {
            errorMessage = `Reserve price must be between ${convertToCurrency(Math.min(
                minValue,
                DEFAULT_MAX_PRICE
            ))} and ${convertToCurrency(Math.min(
                maxValue,
                DEFAULT_MAX_PRICE
            ))}`;
        }

        return this._validatorPipe.transform({
            name: 'Reserve price',
            validations: {
                required: false,
                customValidation: {
                    required: { value: false },
                    enable: true,
                    errorMessage: errorMessage,
                    validate: (val: number) => {
                        if (
                            val == null ||
                            !this.draftData.listingDetails.startPrice ||
                            this.draftData.listingDetails.startPrice === 0
                        ) {
                            return true;
                        }

                        if (isIncreased || isLessThanMaxBidPlusOne || !isValidRange) {
                            return false;
                        }

                        return true;
                    }
                }
            }
        });
    }

    buynowPriceValidation() {
        return this._validatorPipe.transform({
            name: 'Buy Now price',
            validations: {
                required: {
                    value:
                        this.draftData.listingDetails.auctionType ===
                        AuctionTypeEnum.buyNowOnly
                },
                maxValue: {
                    value: DEFAULT_MAX_PRICE
                },
                customValidation: {
                    errorMessage:
                        'Buy Now must not be lower than the Start price',
                    enable:
                        this.draftData.listingDetails.auctionType ===
                        AuctionTypeEnum.auctionAndBuyNow,
                    validate: (buynowPrice: number) => {
                        return (
                            buynowPrice === null ||
                            buynowPrice >=
                            this.draftData.listingDetails.startPrice
                        );
                    }
                }
            }
        });
    }

    updateAuctionType() {
        this.motorsAuctionPricingSectionForm?.form.markAsDirty();
        this.publish();
        this._changeDetectorRef.markForCheck();
    }
}
